// The dev panel's shared furniture.
//
// Every section of the panel is the same shape — a caption, a row or a column
// of controls, and a line of prose saying what the control will do — so the
// shape is said once here and the sections say only what is theirs. Nothing in
// this file knows about the game.

import { createContext, useContext, useState, type ReactNode } from "react";

import { Collapse, Icon, motion, styled } from "../kit";
import { FOLDS_KEY, parseFolds, serialiseFolds, toggleFold } from "./folds";

/**
 * The sheet every section is drawn inside.
 *
 * **A card inside a panel of stacked rows opens beside the panel, never beside
 * its trigger** (CLAUDE.md; the U6 second review). `right` of a control at the
 * left edge of a 360px sheet is still inside that sheet, landing on the row
 * next to it — which is exactly what the grant grid did. So every `HoverCard`
 * here takes `beside={useBesidePanel()}` and hangs off the sheet's own edge.
 *
 * A context rather than a prop, because it would otherwise be threaded through
 * seven components that have no other reason to know they are in a panel.
 */
const PanelContext = createContext<HTMLElement | null>(null);

export const PanelProvider = PanelContext.Provider;

export const useBesidePanel = (): HTMLElement | null => useContext(PanelContext);

/** One block of the panel. */
export const Section = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.kit.space.xs,
  marginBottom: theme.kit.space.md
}));

/** A section's caption — the display voice, because it labels rather than says. */
export const Caption = styled("span")(({ theme }) => ({
  fontFamily: theme.kit.font.display,
  fontWeight: 600,
  fontSize: 10,
  letterSpacing: ".1em",
  textTransform: "uppercase",
  color: theme.kit.color.inkDim
}));

/** A row of controls that share the width. */
export const Row = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: theme.kit.space.xs,
  "& > button": { flex: 1 }
}));

/** A row whose controls keep their own width. */
export const Loose = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: theme.kit.space.xs
}));

/** The line under a control saying what it will do, or why it will not. */
export const Note = styled("span")(({ theme }) => ({
  display: "block",
  fontFamily: theme.kit.font.body,
  fontSize: 11,
  lineHeight: 1.4,
  color: theme.kit.color.inkDim
}));

/**
 * The closed folds, read off the browser once and then held here.
 *
 * One set for every fold rather than one read per fold: each section mounts
 * with its own copy otherwise, and the second toggle writes back a set that
 * never saw the first. `folds.ts` is the rule; this is the storage.
 */
let folded: ReadonlySet<string> | undefined;

function loadFolds(): ReadonlySet<string> {
  if (folded) return folded;
  try {
    folded = parseFolds(window.localStorage?.getItem(FOLDS_KEY));
  } catch {
    folded = new Set();
  }
  return folded;
}

/** Flip one fold and remember it. Answers whether the section is now open. */
function flipFold(id: string): boolean {
  folded = toggleFold(loadFolds(), id);
  try {
    window.localStorage?.setItem(FOLDS_KEY, serialiseFolds(folded));
  } catch {
    // A fold that cannot be remembered still folds for this session.
  }
  return !folded.has(id);
}

/**
 * A section's caption as its handle.
 *
 * The caption keeps its face and gains a chevron; the whole width is the
 * target, since a ten-point caption is not. `aria-expanded` is the state the
 * chevron turns on, the same way the scenario screen's Advanced toggle does.
 */
const FoldHead = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.kit.space.xs,
  width: "100%",
  padding: 0,
  border: 0,
  background: "transparent",
  textAlign: "left",
  cursor: "pointer",
  color: theme.kit.color.inkDim,
  "& > span": { color: "inherit", flex: 1 },
  "& .chevron": motion([], ["transform"]),
  '&:hover, &[aria-expanded="true"]': { color: theme.kit.color.ink },
  '&[aria-expanded="true"] .chevron': { transform: "rotate(90deg)" }
}));

/** The folded body keeps the section's own spacing between its controls. */
const FoldBody = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.kit.space.xs
}));

/**
 * A section that folds shut on its caption, and stays folded across reloads.
 *
 * `id` is the fold's name in storage and its test id (`dev-fold-<id>`), so it
 * is API like every other id here. The body stays mounted while folded — a
 * slider set and then hidden is still set when the section opens again —
 * which is also why a folded control cannot be clicked: it is not on screen.
 */
export function Fold({
  id,
  title,
  children
}: {
  id: string;
  title: ReactNode;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(() => !loadFolds().has(id));
  return (
    <Section>
      <FoldHead
        type="button"
        aria-expanded={open}
        data-testid={`dev-fold-${id}`}
        onClick={() => setOpen(flipFold(id))}
      >
        <Icon name="chevronRight" size={12} className="chevron" />
        <Caption>{title}</Caption>
      </FoldHead>
      <Collapse in={open}>
        <FoldBody>{children}</FoldBody>
      </Collapse>
    </Section>
  );
}
